import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Restaurant as RestaurantIcon,
  Warning as WarningIcon,
  FitnessCenter as FitnessIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import styled from "styled-components";
import { RequestState } from "../../modules/constants";
import { colors } from "../../theme";

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`;

const StyledCardHeader = styled(CardHeader)`
  background: ${colors.gradients.secondary};
  color: white;

  .MuiCardHeader-title {
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ChipContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const SaveButtonContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding: 24px;
  background: ${colors.background.paper};
  border-radius: 12px;
  border: 1px solid #e0e0e0;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

// Common food options for autocomplete
const commonFoods = [
  "Chicken",
  "Beef",
  "Pork",
  "Fish",
  "Salmon",
  "Tuna",
  "Shrimp",
  "Turkey",
  "Rice",
  "Pasta",
  "Bread",
  "Quinoa",
  "Oats",
  "Potatoes",
  "Sweet Potatoes",
  "Broccoli",
  "Spinach",
  "Carrots",
  "Tomatoes",
  "Onions",
  "Garlic",
  "Bell Peppers",
  "Apples",
  "Bananas",
  "Oranges",
  "Berries",
  "Avocado",
  "Lemon",
  "Lime",
  "Cheese",
  "Yogurt",
  "Milk",
  "Eggs",
  "Butter",
  "Olive Oil",
  "Coconut Oil",
  "Beans",
  "Lentils",
  "Chickpeas",
  "Nuts",
  "Almonds",
  "Walnuts",
  "Seeds",
];

// Common allergies
const commonAllergies = [
  "Peanuts",
  "Tree Nuts",
  "Milk",
  "Eggs",
  "Fish",
  "Shellfish",
  "Soy",
  "Wheat",
  "Gluten",
  "Sesame",
  "Mustard",
  "Sulfites",
  "Celery",
  "Lupin",
  "Mollusks",
];

// Dietary goal options
const dietaryGoalOptions = [
  "Weight Loss",
  "Weight Gain",
  "Muscle Building",
  "Athletic Performance",
  "General Health",
  "Heart Health",
  "Diabetes Management",
  "Lower Cholesterol",
  "Increase Energy",
  "Better Digestion",
  "Anti-Inflammatory",
  "Detox",
];

// Activity levels
const activityLevels = [
  { value: 1.2, label: "Sedentary (little to no exercise)" },
  { value: 1.375, label: "Lightly active (light exercise 1-3 days/week)" },
  { value: 1.55, label: "Moderately active (moderate exercise 3-5 days/week)" },
  { value: 1.725, label: "Very active (hard exercise 6-7 days/week)" },
  { value: 1.9, label: "Extremely active (very hard exercise, physical job)" },
];

export const UserPreferencesForm = observer(({ controller }) => {
  const {
    preferences,
    updatePreference,
    savePreferences,
    getSaveMessage,
    getSaveRequestState,
    loadUserPreferences,
  } = controller;

  const [newFavoriteFood, setNewFavoriteFood] = useState("");
  const [newAllergy, setNewAllergy] = useState("");
  const [newGoal, setNewGoal] = useState("");

  useEffect(() => {
    loadUserPreferences();
  }, [loadUserPreferences]);

  const handleSubmit = (e) => {
    e.preventDefault();
    savePreferences();
  };

  const isLoading = getSaveRequestState === RequestState.REQUESTED;

  const addFavoriteFood = () => {
    if (
      newFavoriteFood &&
      !preferences.favoriteFoods.includes(newFavoriteFood)
    ) {
      updatePreference("favoriteFoods", [
        ...preferences.favoriteFoods,
        newFavoriteFood,
      ]);
      setNewFavoriteFood("");
    }
  };

  const removeFavoriteFood = (food) => {
    updatePreference(
      "favoriteFoods",
      preferences.favoriteFoods.filter((f) => f !== food)
    );
  };

  const addAllergy = () => {
    if (newAllergy && !preferences.allergies.includes(newAllergy)) {
      updatePreference("allergies", [...preferences.allergies, newAllergy]);
      setNewAllergy("");
    }
  };

  const removeAllergy = (allergy) => {
    updatePreference(
      "allergies",
      preferences.allergies.filter((a) => a !== allergy)
    );
  };

  const addGoal = () => {
    if (newGoal && !preferences.dietaryGoals.includes(newGoal)) {
      updatePreference("dietaryGoals", [...preferences.dietaryGoals, newGoal]);
      setNewGoal("");
    }
  };

  const removeGoal = (goal) => {
    updatePreference(
      "dietaryGoals",
      preferences.dietaryGoals.filter((g) => g !== goal)
    );
  };

  return (
    <Box>
      {getSaveMessage && (
        <Alert
          severity={
            getSaveMessage.includes("successfully") ? "success" : "error"
          }
          sx={{ mb: 3 }}
        >
          {getSaveMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Favorite Foods Section */}
        <StyledCard>
          <StyledCardHeader
            title={
              <>
                <SectionIcon>
                  <RestaurantIcon />
                </SectionIcon>
                Favorite Foods
              </>
            }
            subheader="Tell us what you love to eat"
          />
          <CardContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add your favorite foods to help us suggest meals you'll enjoy. The
              more you add, the better our AI can personalize your meal plans.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  freeSolo
                  options={commonFoods}
                  value={newFavoriteFood}
                  onChange={(event, newValue) =>
                    setNewFavoriteFood(newValue || "")
                  }
                  onInputChange={(event, newInputValue) =>
                    setNewFavoriteFood(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add a favorite food"
                      placeholder="Type or select from common options"
                      fullWidth
                      disabled={isLoading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={addFavoriteFood}
                  disabled={!newFavoriteFood || isLoading}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Add Food
                </Button>
              </Grid>
            </Grid>

            <ChipContainer>
              {preferences.favoriteFoods.map((food, index) => (
                <Chip
                  key={index}
                  label={food}
                  onDelete={() => removeFavoriteFood(food)}
                  color="primary"
                  variant="outlined"
                  disabled={isLoading}
                />
              ))}
            </ChipContainer>
          </CardContent>
        </StyledCard>

        {/* Allergies Section */}
        <StyledCard>
          <StyledCardHeader
            title={
              <>
                <SectionIcon>
                  <WarningIcon />
                </SectionIcon>
                Allergies & Intolerances
              </>
            }
            subheader="Help us keep you safe"
          />
          <CardContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              List any food allergies or intolerances you have. This is crucial
              for ensuring all meal suggestions are safe for you to consume.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  freeSolo
                  options={commonAllergies}
                  value={newAllergy}
                  onChange={(event, newValue) => setNewAllergy(newValue || "")}
                  onInputChange={(event, newInputValue) =>
                    setNewAllergy(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add an allergy or intolerance"
                      placeholder="Type or select from common allergies"
                      fullWidth
                      disabled={isLoading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={addAllergy}
                  disabled={!newAllergy || isLoading}
                  startIcon={<AddIcon />}
                  fullWidth
                  color="warning"
                >
                  Add Allergy
                </Button>
              </Grid>
            </Grid>

            <ChipContainer>
              {preferences.allergies.map((allergy, index) => (
                <Chip
                  key={index}
                  label={allergy}
                  onDelete={() => removeAllergy(allergy)}
                  color="warning"
                  variant="outlined"
                  disabled={isLoading}
                />
              ))}
            </ChipContainer>
          </CardContent>
        </StyledCard>

        {/* Dietary Goals Section */}
        <StyledCard>
          <StyledCardHeader
            title={
              <>
                <SectionIcon>
                  <FitnessIcon />
                </SectionIcon>
                Dietary Goals & Lifestyle
              </>
            }
            subheader="What are you trying to achieve?"
          />
          <CardContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Tell us about your health and fitness goals so we can create meal
              plans that support your objectives.
            </Typography>

            {/* Goals */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Health Goals
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    freeSolo
                    options={dietaryGoalOptions}
                    value={newGoal}
                    onChange={(event, newValue) => setNewGoal(newValue || "")}
                    onInputChange={(event, newInputValue) =>
                      setNewGoal(newInputValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add a health goal"
                        placeholder="Select or type your goal"
                        fullWidth
                        disabled={isLoading}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={addGoal}
                    disabled={!newGoal || isLoading}
                    startIcon={<AddIcon />}
                    fullWidth
                    color="success"
                  >
                    Add Goal
                  </Button>
                </Grid>
              </Grid>

              <ChipContainer>
                {preferences.dietaryGoals.map((goal, index) => (
                  <Chip
                    key={index}
                    label={goal}
                    onDelete={() => removeGoal(goal)}
                    color="success"
                    variant="outlined"
                    disabled={isLoading}
                  />
                ))}
              </ChipContainer>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Physical Information */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Physical Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Current Weight (lbs)"
                  type="number"
                  value={preferences.currentWeight}
                  onChange={(e) =>
                    updatePreference("currentWeight", e.target.value)
                  }
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Target Weight (lbs)"
                  type="number"
                  value={preferences.targetWeight}
                  onChange={(e) =>
                    updatePreference("targetWeight", e.target.value)
                  }
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Height (inches)"
                  type="number"
                  value={preferences.height}
                  onChange={(e) => updatePreference("height", e.target.value)}
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isLoading}>
                  <InputLabel>Activity Level</InputLabel>
                  <Select
                    value={preferences.activityLevel}
                    onChange={(e) =>
                      updatePreference("activityLevel", e.target.value)
                    }
                    label="Activity Level"
                  >
                    {activityLevels.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Daily Targets */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Daily Nutrition Targets
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Daily Calories: {preferences.dailyCalories}
                </Typography>
                <Slider
                  value={preferences.dailyCalories}
                  onChange={(e, value) =>
                    updatePreference("dailyCalories", value)
                  }
                  min={1200}
                  max={4000}
                  step={50}
                  marks={[
                    { value: 1200, label: "1200" },
                    { value: 2000, label: "2000" },
                    { value: 3000, label: "3000" },
                    { value: 4000, label: "4000" },
                  ]}
                  valueLabelDisplay="auto"
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Protein (g)"
                  type="number"
                  value={preferences.dailyProtein}
                  onChange={(e) =>
                    updatePreference("dailyProtein", e.target.value)
                  }
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Carbs (g)"
                  type="number"
                  value={preferences.dailyCarbs}
                  onChange={(e) =>
                    updatePreference("dailyCarbs", e.target.value)
                  }
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Fat (g)"
                  type="number"
                  value={preferences.dailyFat}
                  onChange={(e) => updatePreference("dailyFat", e.target.value)}
                  fullWidth
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Dietary Restrictions */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dietary Preferences
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isVegetarian}
                      onChange={(e) =>
                        updatePreference("isVegetarian", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Vegetarian"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isVegan}
                      onChange={(e) =>
                        updatePreference("isVegan", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Vegan"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isKeto}
                      onChange={(e) =>
                        updatePreference("isKeto", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Ketogenic"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isPaleo}
                      onChange={(e) =>
                        updatePreference("isPaleo", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Paleo"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isGlutenFree}
                      onChange={(e) =>
                        updatePreference("isGlutenFree", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Gluten-Free"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.isDairyFree}
                      onChange={(e) =>
                        updatePreference("isDairyFree", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                  }
                  label="Dairy-Free"
                />
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        <SaveButtonContainer>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Your preferences help our AI create personalized meal plans just
              for you.
            </Typography>
          </Box>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            sx={{
              background: colors.gradients.primary,
              padding: "12px 32px",
              fontSize: "1.1rem",
            }}
          >
            {isLoading ? "Saving..." : "Save Preferences"}
          </Button>
        </SaveButtonContainer>
      </form>
    </Box>
  );
});

export default UserPreferencesForm;
